import { useSyncExternalStore } from "react";

const INTERVAL_MS = 2000;
const FETCH_TIMEOUT_MS = 8000;

function pingUrl(): string {
    const raw = process.env.REACT_APP_PING_URL?.trim();
    const fallback =
        typeof window !== "undefined" && window.location?.origin
            ? `${window.location.origin}/`
            : "http://127.0.0.1/";

    if (!raw) {
        return fallback;
    }

    try {
        const u = new URL(raw);
        // Browsers / extensions often block http://0.0.0.0/ (net::ERR_BLOCKED_BY_CLIENT).
        if (u.hostname === "0.0.0.0" && typeof window !== "undefined" && window.location?.origin) {
            return `${window.location.origin}/`;
        }
    } catch {
        return raw;
    }
    return raw;
}

async function measurePingMs(): Promise<number | null> {
    const url = pingUrl();
    const t0 = performance.now();
    const controller = new AbortController();
    const to = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
        await fetch(url, {
            method: "GET",
            mode: "no-cors",
            cache: "no-store",
            signal: controller.signal,
        });
    } catch (e) {
        if (
            (e instanceof DOMException || e instanceof Error) &&
            e.name === "AbortError"
        ) {
            return null;
        }
        /* Other failures still yield a round-trip time */
    } finally {
        window.clearTimeout(to);
    }
    const dt = performance.now() - t0;
    if (dt >= FETCH_TIMEOUT_MS - 50) {
        return null;
    }
    return Math.round(dt);
}

export type ZeroHostPingState = {
    /** Milliseconds to complete the probe, or null if timed out */
    ms: number | null;
    /** True until the first measurement finishes */
    pending: boolean;
};

let pingState: ZeroHostPingState = { ms: null, pending: true };
const listeners = new Set<() => void>();

function emit() {
    listeners.forEach((l) => l());
}

let timer: number | null = null;

function tick() {
    void (async () => {
        const ms = await measurePingMs();
        pingState = { ms, pending: false };
        emit();
    })();
}

function startTimer() {
    if (timer != null) return;
    tick();
    timer = window.setInterval(tick, INTERVAL_MS);
}

function stopTimer() {
    if (timer != null) {
        window.clearInterval(timer);
        timer = null;
    }
}

function subscribe(onStoreChange: () => void) {
    listeners.add(onStoreChange);
    if (listeners.size === 1) {
        pingState = { ms: null, pending: true };
        startTimer();
    }
    return () => {
        listeners.delete(onStoreChange);
        if (listeners.size === 0) {
            stopTimer();
        }
    };
}

function getSnapshot(): ZeroHostPingState {
    return pingState;
}

function getServerSnapshot(): ZeroHostPingState {
    return { ms: null, pending: true };
}

/**
 * Periodic HTTP probe toward `REACT_APP_PING_URL` or the current page origin (ICMP is not available in browsers).
 * Avoids `http://0.0.0.0/` by default — many extensions block it (ERR_BLOCKED_BY_CLIENT).
 * Interval: 2s. One shared timer for all subscribers.
 */
export function useZeroHostPing(): ZeroHostPingState {
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function formatPingMs(state: ZeroHostPingState): string {
    if (state.pending || state.ms == null) return "- ms";
    return `${state.ms} ms`;
}

/** True when the HTTP probe completed with a measured RTT (not timed out). */
export function isZeroHostPingReachable(state: ZeroHostPingState): boolean {
    return !state.pending && state.ms !== null;
}
